import { css } from 'lit';
import componentStyles from '../styles/component.styles';
export default css `
  ${componentStyles}
  /**
--------------------------------------------------
* General
--------------------------------------------------
*/
:host {
    display: inline-block;
    width: auto;
  }

  .button {
    display: flex;
    padding: 0 var(--p-button-padding-inline);
    min-height: var(--p-input-height);
    align-items: center;
    justify-content: center;
    border-radius: var(--p-button-border-radius);
    transition:
      background 0.25s ease,
      border 0.25s ease,
      color 0.25s ease;
    cursor: pointer;
    font-family: var(--p-button-font);
    font-size: var(--p-button-font-size);
    font-weight: var(--p-button-font-weight);
    font-style: var(--p-button-font-style);
    text-transform: var(--p-button-transform);
    letter-spacing: var(--p-button-letter-spacing);
    width: 100%;
    /* white-space: nowrap; */
    border: var(--p-button-border-width) solid transparent;
    text-decoration: none;
    position: relative;
    margin: 0;
  }

  .slot-wrapper {
    display: inline;
    position: relative;
    z-index: 2;
  }

  slot::slotted(svg) {
    pointer-events: none;
    display: block;
  }

  /**
--------------------------------------------------
* Block
--------------------------------------------------
*/
  :host([block]) {
    display: block;
  }

  :host([block]) .button {
    padding-left: 0;
    padding-right: 0;
  }

  /**
--------------------------------------------------
* Types
--------------------------------------------------
*/
  /* Primary */
  .primary {
    background: var(--p-button-primary-bg);
    color: var(--p-button-primary-text-color);
    border-color: var(--p-button-primary-border-color);
  }

  .primary:hover {
    background: var(--p-button-primary-bg-hover);
    color: var(--p-button-primary-text-color-hover);
    border-color: var(--p-button-primary-border-color-hover);
  }

  .primary:active {
    background: var(--p-button-primary-bg);
  }

  /* Secondary */
  .secondary {
    background: var(--p-button-secondary-bg);
    color: var(--p-button-secondary-text-color);
    border-color: var(--p-button-secondary-border-color);
  }

  .secondary:hover {
    background: var(--p-button-secondary-bg-hover);
    color: var(--p-button-secondary-text-color-hover);
    border-color: var(--p-button-secondary-border-color-hover);
  }

  .secondary:active {
    background: var(--p-button-secondary-bg);
  }

  /* Border */
  .border {
    background: var(--p-bg);
    border-width: var(--p-button-border-min-width);
    color: var(--p-text-color);
    border-color: var(--p-border);
  }

  .border:hover {
    background: var(--p-text-color-contrast-100);
  }

  .border:active {
    background: var(--p-bg);
  }

  /* Border dark */
  :host([dark]) .border {
    background: var(--p-bg-dark);
    border-color: var(--p-text-color-light-contrast-300);
    color: var(--p-text-color-light);
  }

  :host([dark]) .border:hover {
    background: var(--p-text-color-light-contrast-100);
  }

  :host([dark]) .border:active {
    background: var(--p-bg-dark);
  }

  /* Ghost */
  .ghost {
    background: transparent;
    color: var(--p-text-color);
    border-color: transparent;
  }

  .ghost:hover {
    background: var(--p-text-color-contrast-100);
  }

  .ghost:active {
    background: transparent;
  }

  /* Arrow */
  .arrow {
    color: var(--p-link-text-color);
    border-color: transparent;
    background: transparent;
    padding-left: 0;
    padding-right: 0;
    border-radius: 0;
    line-height: 1.4;
    min-height: 0;
    white-space: nowrap;
    text-decoration: none;
  }

  .arrow::after {
    /* increase click area around the button */
    content: '';
    position: absolute;
    display: block;
    top: -10px;
    bottom: -10px;
    left: -10px;
    right: -10px;
  }
  @keyframes arrow-shake {
    0%,
    15%,
    30%,
    100% {
      transform: translate3d(0, 0, 0);
    }
    10%,
    20% {
      transform: translate3d(2px, 0, 0);
    }
  }

  .arrow:hover .slot-wrapper > svg {
    animation: arrow-shake 3s infinite ease-in-out;
  }

  .arrow .slot-text {
    text-decoration: var(--p-link-text-decoration);
  }

  .arrow:hover {
    color: var(--p-link-text-color-hover);
  }

  .arrow:hover .slot-text {
    text-decoration: var(--p-link-text-decoration-hover);
  }

  @media (prefers-reduced-motion) {
    .arrow:hover .slot-wrapper > svg {
      animation: none;
    }
  }

  /* Link */
  .link {
    background: transparent;
    color: var(--p-link-text-color);
    text-decoration: var(--p-link-text-decoration);
    border-width: 0;
    padding: 0 !important;
    border-radius: 0;

    white-space: unset;
    text-align: left;
    min-height: var(--p-input-height);
    line-height: 1.1;
  }

  .link:hover {
    color: var(--p-link-text-color-hover);
    text-decoration: var(--p-link-text-decoration-hover);
  }

  /**
--------------------------------------------------
* Sizes
--------------------------------------------------
*/
  .small {
    --p-input-height: var(--p-input-height-small);
    --p-button-font-size: var(--p-button-small-font-size, 0.75rem);
  }

  /**
--------------------------------------------------
* Slots
--------------------------------------------------
*/
  slot[name='prefix']::slotted(svg),
  slot[name='prefix']::slotted(p-icon) {
    margin-right: 0.5rem;
  }

  slot[name='suffix']::slotted(svg),
  slot[name='suffix']::slotted(p-icon) {
    margin-left: 0.5rem;
  }

  /**
--------------------------------------------------
* Caret
--------------------------------------------------
*/
  .caret {
    margin-left: 0.2em;
    max-width: 11px;
    transform: translateY(0.1em);
  }

  .small .caret {
    max-width: 10px;
  }

  [aria-expanded='true'] .caret {
    transform: rotate(180deg);
  }

  /**
--------------------------------------------------
* Icon
--------------------------------------------------
*/
  .icon {
    width: var(--p-input-height);
    padding: 0;
    border-radius: var(--p-button-icon-border-radius, 5px);
  }

  /**
--------------------------------------------------
* Disabled
--------------------------------------------------
*/
  [disabled] {
    pointer-events: none;
    background-color: var(--p-border) !important;
    border-color: var(--p-border) !important;
    border-color: transparent !important;
    color: var(--p-text-color-meta) !important;
  }

  /**
--------------------------------------------------
* Loading
--------------------------------------------------
*/
  .loading {
    position: relative;
    color: transparent !important;
    pointer-events: none;
    overflow: hidden;
    background-color: var(--p-bg-light);
    border-color: transparent;
  }

  .loading:focus {
    box-shadow: none;
  }

  .loading::after,
  .loading::before {
    content: '';
    position: absolute;
    width: 7px;
    height: 7px;
    background-color: var(--p-text-color);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    opacity: 0.2;
    transform: translate(-50%, -50%) scale(0);
    animation: button-load 1.2s infinite ease-in-out;
  }

  .loading::before {
    animation-delay: 0.4s;
  }

  @keyframes button-load {
    0% {
      transform: translate(-50%, -50%) scale(0);
      border-radius: 50%;
      opacity: 0.2;
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(8);
    }
  }

  /**
--------------------------------------------------
* Active
--------------------------------------------------
*/
  .active {
    background: var(--p-brand-active-bg);
    color: var(--p-text-color);
    border-color: var(--p-brand);
  }

  /**
--------------------------------------------------
  * Success animation
--------------------------------------------------
*/
  .success {
    position: relative;
    color: transparent !important;
    pointer-events: none;
    overflow: hidden;
    background-color: transparent;
    border-color: transparent;
    transition:
      color 0s linear,
      background-color 0.25s ease,
      border-color 0.25s ease;
  }

  .success::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: var(--p-success);
    border-radius: var(--p-button-border-radius);
    opacity: 0.2;
    animation: fade-in 0.25s forwards ease-in-out;
  }

  .success.icon::before {
    border-radius: 5px;
  }

  .success-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    transition: all 0.3s ease-in-out 0.2s;
    color: var(--p-success);
  }

  .success .success-icon {
    transform: translate(-50%, -50%) scale(1);
  }

  .success::after {
    content: '';
    position: absolute;
    width: 7px;
    height: 7px;
    background-color: var(--p-success);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    opacity: 0.2;
    transform: translate(-50%, -50%) scale(0);
    animation: button-load 0.8s forwards ease-in-out;
  }

  @keyframes fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 0.2;
    }
  }

  @keyframes button-success {
    0% {
      transform: translate(-50%, -50%) scale(0);
      border-radius: 50%;
      opacity: 0.2;
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(8);
    }
  }

  /**
--------------------------------------------------
* Button group
--------------------------------------------------
*/
  :host(.button-group-item) .button {
    border-radius: 0;
    border-left-color: rgba(255, 255, 255, 0.2);
  }
  :host(.button-group-item-first) .button {
    border-radius: 5px 0 0 5px;
    border-left-color: transparent;
  }
  :host(.button-group-item-last) .button {
    border-radius: 0 5px 5px 0;
  }

  :host(.button-group-item) .button.border {
    border-right-color: transparent;
    border-left-color: var(--p-border);
  }
  :host(.button-group-item-last) .button.border {
    border-right-color: var(--p-border);
  }
  :host(.button-group-item) .button.border.active {
    border-left-color: var(--p-brand);
  }

  :host(.button-group-item) .button.border.active::after {
    content: '';
    position: absolute;
    width: 1px;
    height: 100%;
    top: 0;
    right: -1px;
    background: var(--p-brand);
  }
  :host(.button-group-item-last) .button.border.active {
    border-right-color: var(--p-brand);
  }

  :host(.button-group-item-last) .button.border.active::after {
    display: none;
  }
`;
